import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getStepIndex, steps } from '../config/steps';
import { usePlatform } from '../hooks/usePlatform';

export const Navigation: React.FC = () => {
  const location = useLocation();
  const currentStep = steps.find(step => step.path === location.pathname);
  const currentIndex = currentStep ? getStepIndex(currentStep.id) : -1;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { platform, setPlatform } = usePlatform();

  // Don't show navigation on home page
  if (location.pathname === '/') {
    return null;
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const togglePlatform = () => {
    setPlatform(platform === 'ios' ? 'android' : 'ios');
  };

  return (
    <nav className='nav'>
      <div className='nav-container'>
        <div className='nav-content'>
          <div className='flex items-center'>
            <Link to='/' className='nav-brand font-display'>
              PUMPAJ!
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className='nav-links'>
            {steps.map((step, index) => {
              const isActive = step.path === location.pathname;
              const isCompleted = index < currentIndex;
              const isAccessible =
                index <= currentIndex + 1 || currentIndex === -1;

              return (
                <Link
                  key={step.id}
                  to={step.path}
                  className={`nav-link ${
                    isActive
                      ? 'active'
                      : isCompleted
                        ? 'completed'
                        : isAccessible
                          ? ''
                          : 'disabled'
                  }`}
                  onClick={e => {
                    if (!isAccessible) {
                      e.preventDefault();
                    }
                  }}
                >
                  <span className='flex items-center space-x-2'>
                    <span
                      className={`step-indicator ${
                        isActive
                          ? 'active'
                          : isCompleted
                            ? 'completed'
                            : isAccessible
                              ? ''
                              : 'disabled'
                      }`}
                    >
                      {isCompleted ? '✓' : index + 1}
                    </span>
                    <span className='hidden lg:inline'>{step.title}</span>
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Platform toggle button */}
          {platform && (
            <div className='platform-indicator'>
              <button
                onClick={togglePlatform}
                className='text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 px-3 py-2 rounded cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500'
                title={`Currently ${platform === 'ios' ? 'iOS' : 'Android'}. Click to switch to ${platform === 'ios' ? 'Android' : 'iOS'}.`}
              >
                {platform === 'ios' ? '📱 iOS' : '🤖 Android'}
              </button>
            </div>
          )}

          {/* Mobile burger menu button */}
          <div className='md:hidden'>
            <button
              onClick={toggleMobileMenu}
              className='burger-menu-button'
              aria-label='Toggle navigation menu'
            >
              <div
                className={`burger-line ${isMobileMenuOpen ? 'open' : ''}`}
              ></div>
              <div
                className={`burger-line ${isMobileMenuOpen ? 'open' : ''}`}
              ></div>
              <div
                className={`burger-line ${isMobileMenuOpen ? 'open' : ''}`}
              ></div>
            </button>
          </div>
        </div>

        {/* Mobile navigation menu */}
        <div className={`nav-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className='nav-mobile-progress'>
            Korak {currentIndex + 1} od {steps.length}
            {platform && (
              <button
                onClick={togglePlatform}
                className='ml-4 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 px-2 py-1 rounded cursor-pointer transition-colors duration-200'
                title={`Currently ${platform === 'ios' ? 'iOS' : 'Android'}. Click to switch to ${platform === 'ios' ? 'Android' : 'iOS'}.`}
              >
                {platform === 'ios' ? '📱 iOS' : '🤖 Android'}
              </button>
            )}
          </div>
          <div className='nav-mobile-links'>
            {steps.map((step, index) => {
              const isActive = step.path === location.pathname;
              const isCompleted = index < currentIndex;
              const isAccessible =
                index <= currentIndex + 1 || currentIndex === -1;

              return (
                <Link
                  key={step.id}
                  to={step.path}
                  className={`nav-mobile-link ${
                    isActive
                      ? 'active'
                      : isCompleted
                        ? 'completed'
                        : isAccessible
                          ? ''
                          : 'disabled'
                  }`}
                  onClick={e => {
                    if (!isAccessible) {
                      e.preventDefault();
                    } else {
                      setIsMobileMenuOpen(false);
                    }
                  }}
                >
                  <span className='flex items-center space-x-3'>
                    <span
                      className={`step-indicator ${
                        isActive
                          ? 'active'
                          : isCompleted
                            ? 'completed'
                            : isAccessible
                              ? ''
                              : 'disabled'
                      }`}
                    >
                      {isCompleted ? '✓' : index + 1}
                    </span>
                    <span>{step.title}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};
